import { Suspense, lazy } from 'react';
import { Navigate, useParams, type RouteObject } from 'react-router-dom';
import { RequirePermission } from '../shared/guards/RequirePermission';
import {
  HITOWA_RBAC_SETTINGS_PATH,
  HITOWA_RBAC_SETTINGS_SCOPE,
  HITOWA_RBAC_SETTINGS_VIEW_PERMISSION,
} from '../pages/rbac-settings/access';

export const HITOWA_PATIENT_MANAGEMENT_PATH = '/patient-management';

/**
 * Hitowa clinic-portal routes, merged into the shared clinic router and only
 * reachable under `/hitowa/...`.
 *
 * Screens are loaded lazily, and not only for the bundle split: a screen pulls
 * in shared UI that reaches back to `customize/registry` through the theme
 * context, and this module is itself imported by the tenant bundle the registry
 * builds. A static import closes that loop and the screen resolves to
 * `undefined` at render. `lazy` defers the import past module evaluation.
 */
const PatientManagementPage = lazy(
  () => import('../pages/patient-management/pages/PatientManagementPage')
);

const RbacSettingsPage = lazy(
  () => import('../pages/rbac-settings/pages/RbacSettingsPage')
);

const LegacyPrefixRedirect = () => {
  const { tenant } = useParams<{ tenant: string }>();
  return <Navigate to={tenant ? `/${tenant}` : '/'} replace />;
};

export const hitowaClinicRoutes: RouteObject[] = [
  // Old HITOWA concepts used main/checkin as portal prefixes. They are aliases
  // only: the unified tenant never renders a second shell at those URLs.
  { path: 'main/*', element: <LegacyPrefixRedirect /> },
  { path: 'checkin/*', element: <LegacyPrefixRedirect /> },
  {
    // 利用者管理 — 利用者一覧 (保険証) / 同意書一覧 / 撮影受領キュー. One path for
    // all three: the ConceptDesign keeps them on one page behind tabs, and the
    // 同意書 of a 利用者 is not a separate destination.
    path: HITOWA_PATIENT_MANAGEMENT_PATH.slice(1),
    element: (
      <RequirePermission menuPath={HITOWA_PATIENT_MANAGEMENT_PATH}>
        <Suspense fallback={null}>
          <PatientManagementPage />
        </Suspense>
      </RequirePermission>
    ),
  },
  {
    path: HITOWA_RBAC_SETTINGS_PATH.slice(1),
    element: (
      <RequirePermission
        menuPath={HITOWA_RBAC_SETTINGS_PATH}
        permissions={[HITOWA_RBAC_SETTINGS_VIEW_PERMISSION]}
        requireRbacEnabled
        action="view"
        scope={HITOWA_RBAC_SETTINGS_SCOPE}
      >
        <Suspense fallback={null}>
          <RbacSettingsPage />
        </Suspense>
      </RequirePermission>
    ),
  },
];

export default hitowaClinicRoutes;
